import {Component, OnDestroy, OnInit} from '@angular/core';
import {ActivatedRoute, Router} from '@angular/router';
import {HeaderStep} from '@enum/header.step';
import {HeaderService} from '@service/header/header.service';
import {CommonService} from '@service/common/common.service';
import {ModelService} from '@service/model/model.service';
import {ModelDetail} from '@interface/model-detail/model-detail';
import {ModelElement} from '@interface/model-items/model-element';
import {EtoUtilsService} from '@service/eto/eto-utils.service';
import {ProformaSection} from '@enum/proformaSection';
import {EtoRequestModalComponent} from '../../components/eto/eto-request-modal/eto-request-modal.component';
import {switchMap, takeUntil, tap} from 'rxjs/operators';
import {Subject} from 'rxjs';
import {OrderService} from '@app/services/order/order.service';
import {NavigationService} from '@service/navigation/navigation.service';
import {AuthService} from "@service/auth/auth.service";

@Component({
    selector: 'app-model-detail-grid',
    templateUrl: './model-detail-grid.component.html',
    styleUrls: ['./model-detail-grid.component.scss'],
    standalone: false
})
export class ModelDetailGridComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject();
  public catalog: any = {};
  public overflow = true;
  public loading: boolean;
  public productId: string | undefined;
  public categoryId: string | undefined;
  public etoId: string | undefined;
  public model: ModelDetail | null = null;
  public modelElements: ModelElement[] = [];
  public eto: any = null;
  public filteredItems: any = null;
  public searchText: any = null;

  constructor(private router: Router,
              private activatedRoute: ActivatedRoute,
              private headerService: HeaderService,
              private modelService: ModelService,
              private common: CommonService,
              private etoUtils: EtoUtilsService,
              private orderService: OrderService,
              private navigation: NavigationService,
              private authService: AuthService) {
    this.loading = true;
  }

  ngOnInit(): void {
    this.headerService.header.next(HeaderStep.MODEL_TECHNICAL);
    let parameters = {};
    this.activatedRoute.params.pipe(
      tap(((params: any) => {
        if (params?.id) {
          this.productId = params?.id;
        }
        if (+params?.categoryId && params?.categoryId !== ProformaSection.ETO) {
          this.categoryId = params?.categoryId;
        }
        if (params?.etoId) {
          this.etoId = params?.etoId;
        }
        parameters = {
          modelId: Number(this.productId),
          categoryId: this.categoryId !== ProformaSection.ETO && !!this.categoryId ? Number(this.categoryId) : undefined
        };
      })),
      switchMap(() => this.modelService.modelDetail(parameters)),
      tap(data => {
        if (data?.error) {
          this.common.showErrorToast(data?.error);
        } else {
          this.model = data?.productModel;
        }
      }),
      switchMap(() => this.modelService.modelItems(parameters)),
      tap(data => {
        if (data?.error) {
          this.common.showErrorToast(data?.error);
        } else {
          this.eto = data?.modelEto;
          this.modelElements = data?.modelItems;
          this.assignCopy();
        }
      }),
      takeUntil(this.destroy$))
      .subscribe(() => {});
  }

  ngOnDestroy(): void {
    this.destroy$.next(undefined);
    this.destroy$.complete();
  }

  public openEtoRequestModal(): void {
    const mockProductlId = this.eto || this.modelElements && this.modelElements.length > 0 ? this.modelElements[0] : {productId: 1};
    this.etoUtils.openEtoRequestModal(mockProductlId, EtoRequestModalComponent);
  }

  public assignCopy(): void {
    this.filteredItems = Object.assign([], this.modelElements);
    this.loading = false;
  }

  public modelDetail(): void {
    if (this.productId && this.categoryId) {
      this.headerService.header.next(HeaderStep.MODEL);
      this.router.navigate(['model-detail', String(this.productId), String(this.categoryId)]);
    }
  }

  public convertProductImage(model: any, skip?: boolean): string {
    return this.common.convertProductImage(model, skip);
  }

  public goToCatalogList(): void {
    this.navigation.goToCatalogList(false);
  }

  public goToModelDetailList(): void {
    this.router.navigate(
      ['model-detail-list', this.productId, this.categoryId]
        .filter((x) => x)
        .map((x) => String(x))
    );
  }

  public composerUserAllowed(): boolean {
    return this.authService.currentUserPermission.composerAllowed;
  }

  public configureModelElement(modelElement: ModelElement): void {
    this.headerService.header.next(HeaderStep.CONFIGURATION);
    if (this.etoId) {
      this.router.navigate(['model-configuration', String(modelElement.productId), ProformaSection.ETO, String(this.etoId)]);
    } else {
      this.router.navigate(['model-configuration', String(modelElement.productId)]);
    }
  }

  public filterItem(value: any): void {
    if (!value) {
      this.assignCopy();
    }
    this.filteredItems = Object.assign([], this.modelElements).filter((item: any) => {
      const code = item?.productCode;
      const finalName = item?.productName.concat('-' + code);
      return finalName.toLowerCase().indexOf(value.toLowerCase()) > -1;
    });
  }

  public imageLoaded(model: any): void {
    model.imageLoaded = true;
  }

  public isCopyingConfiguration(): boolean {
    return !!this.orderService.copyConfigurationId;
  }

  public cancelCopyingConfiguration(): void {
    this.orderService.copyConfigurationId = null;
  }

  public clearInput(): void {
    this.searchText = '';
    this.filterItem('');
  }

  public get getModelObj3DUrl(): string {
    return `${this.model?.modelObj3DUrl}?user=${this.authService.currentEmail}` || '';
  }

  public getModelElementObj3DUrl(modelElement: any): string {
    return `${modelElement.modelObj3DUrl}?user=${this.authService.currentEmail}` || '';
  }

}
